import { Checkbox } from "antd";
import PropTypes from "prop-types";
import React from "react";
import ItemActionsMenu from "./ItemActionsMenu";

/**
 * @typedef {Object} SuggestionItem
 * @property {number|string} id
 * @property {string} tip
 * @property {boolean} [isCustom]  // owner-only, admin items are false/undefined
 */

/**
 * Single Quick Tip row rendered as a checkbox (replaces the old Switch toggle).
 * Custom (user-created) items get a UserOutlined marker per EAI-1497 AC #5, plus
 * optional inline edit/delete affordances (owner-only) when the caller passes the
 * handlers — used by the Customize AI Response drawer's inline management.
 *
 * @param {{ item: SuggestionItem, checked: boolean,
 *           onToggle: (item: SuggestionItem) => void, disabled?: boolean,
 *           onEdit?: (item: SuggestionItem) => void,
 *           onDelete?: (item: SuggestionItem) => void }} props
 */
export default function QuickTipCheckbox({
  item,
  checked,
  onToggle,
  disabled = false,
  onEdit = null,
  onDelete = null,
}) {
  const showActions = item.isCustom && onEdit && onDelete;
  return (
    <label className="flex items-center gap-2 w-full py-1 cursor-pointer group">
      <Checkbox checked={checked} disabled={disabled} onChange={() => onToggle(item)} />
      <span className="text-sm text-grey-700">{item.tip}</span>
      {showActions && (
        <span className="ml-auto">
          <ItemActionsMenu onEdit={() => onEdit(item)} onDelete={() => onDelete(item)} />
        </span>
      )}
    </label>
  );
}

QuickTipCheckbox.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    tip: PropTypes.string.isRequired,
    isCustom: PropTypes.bool,
  }).isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

QuickTipCheckbox.defaultProps = {
  disabled: false,
  onEdit: null,
  onDelete: null,
};
